/**
 * Staff handbook content model (STAFF_GUIDE_PLAN.md). Articles are plain data reviewed in pull
 * requests: no CMS. Text may hold `{name}` placeholders filled from live settings when the guide is
 * served (GUIDE_VAR_DEFAULTS), so an article never quotes a level, hour or limit the platform no
 * longer uses.
 */
import { COPY_LIMITS, LEAD_TRADER_REQUIREMENTS, MASTER_LIMITS } from '../copy.js';
import { fmtUsd } from '../format.js';
import { REVIEW_HOURS } from '../onboarding.js';
import { ACADEMY_VAR_DEFAULTS } from '../academy/types.js';
import { DEPOSIT_MIN_USD, RESTRICTED_COUNTRY_NAMES } from '../deposits.js';
import { DEFAULT_FEATURE_SETTINGS } from '../features.js';
import { DEFAULT_WITHDRAW_LIMITS } from '../payouts.js';
import { REWARD_TIERS } from '../rewards.js';
import { SOF_STATEMENT_MONTHS } from '../account.js';
import { LOCK_DEFAULTS, SMART_LIMITS } from '../smart-portfolios.js';
import { DEFAULT_CRYPTO_PRO_SETTINGS } from '../crypto-pro.js';
import { DEFAULT_MEME_RULES, MEME_ASSESSMENT, MEME_PASS_MARK, MEME_RETRY_HOURS } from '../meme.js';
import { DEFAULT_PUSH_SETTINGS } from '../push.js';
import { DEFAULT_AGENT_MESSAGE_SETTINGS } from '../agent-messages.js';
import { DEFAULT_ROUTING } from '../providers.js';
import { VERIFY_DEFAULT_HOURS } from '../wallet-verification.js';
import { ACADEMY_TRACKS } from '../academy/index.js';
import { GUIDE_SAMPLE_CATEGORIES, GUIDE_SAMPLE_INSTRUMENTS, guideFeatureRows, guideLockTerms, guideMarketVars, guideNetworkRows, guidePoolRows, guideRoutingRows, guideRows, guideStandardFeeRows, guideTierRows, guideWithdrawRows, guideYieldRows, } from './vars.js';
/** The desk staff roles (apps/api/src/desk/permissions.ts STAFF_ROLES), in the same order. */
export const GUIDE_ROLES = ['support', 'dealer', 'senior_dealer', 'finance', 'compliance', 'admin', 'retention_dealer'];
export const isGuideRole = (r) => typeof r === 'string' && GUIDE_ROLES.includes(r);
export const GUIDE_ROLE_LABEL = {
    support: 'Support',
    dealer: 'Dealer',
    senior_dealer: 'Senior dealer',
    finance: 'Finance',
    compliance: 'Compliance',
    admin: 'Admin',
    retention_dealer: 'Retention dealer',
};
/** In the order they are shown. */
export const GUIDE_SECTIONS = [
    { id: 'basics', title: 'Basics' },
    { id: 'trading', title: 'Trading rules' },
    { id: 'desk', title: 'Dealer desk' },
    { id: 'masters', title: 'Master accounts' },
    { id: 'risk', title: 'Risk & exposure' },
    { id: 'lifecycle', title: 'Client lifecycle' },
    { id: 'products', title: 'Products' },
    { id: 'admin', title: 'Admin settings' },
];
/**
 * Money permissions the guide quotes the reader's own limits for (desk/permissions.ts PERMISSIONS
 * with `money: true`; the API test checks the two lists match). Each gives two placeholders:
 * `myDirect<Name>` and `myApprove<Name>`, e.g. `{myDirectMoneyDeposit}` for `money.deposit`.
 */
export const GUIDE_LIMIT_PERMISSIONS = [
    'position.correction',
    'money.deposit',
    'money.withdraw',
    'money.credit_in',
    'money.credit_out',
    'money.zero_balance',
    'money.rollover',
    'money.balance_transfer',
    'trade.transfer',
    'trade.edit',
    'trade.restore',
];
/**
 * The other desk permissions (no amount): each gives `myAct<Name>`, what the reader's role may do with
 * it ("direct", "request" or "not allowed"). With GUIDE_LIMIT_PERMISSIONS this is every permission the
 * desk knows (the API test checks it).
 */
export const GUIDE_ACT_PERMISSIONS = ['accounts.view', 'client.reveal', 'account.terms', 'trade.dealer', 'data.export', 'audit.read', 'risk.view'];
/** `money.credit_in` → `MoneyCreditIn`. */
export const guidePermVar = (perm) => perm
    .split(/[._]/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join('');
const pct = (fraction) => `${Number((Number(fraction) * 100).toFixed(2))}%`;
/** Placeholders an article may use, with the value used when the live setting can't be read. */
export const GUIDE_VAR_DEFAULTS = {
    ...ACADEMY_VAR_DEFAULTS,
    marginWarning: ACADEMY_VAR_DEFAULTS.warning,
    marginCritical: ACADEMY_VAR_DEFAULTS.critical,
    /** Where client back-office work is decided (BACKOFFICE_IN_CRM). */
    backofficeHome: 'the CRM',
    demoBalance: '$10,000',
    kycReviewHours: String(REVIEW_HOURS),
    restoreDays: '90',
    fromChartDays: '90',
    refEqMin: fmtUsd(MASTER_LIMITS.minReferenceEquity, 0),
    refEqMax: fmtUsd(MASTER_LIMITS.maxReferenceEquity, 0),
    multMin: `${MASTER_LIMITS.minMultiplier}×`,
    multMax: `${MASTER_LIMITS.maxMultiplier}×`,
    mgmtFeeMax: pct(MASTER_LIMITS.maxMgmtFeePct),
    perfFeeMax: pct(MASTER_LIMITS.maxPerfFeePct),
    /** The reader's staff role. */
    myRole: GUIDE_ROLE_LABEL.dealer,
    ...Object.fromEntries(GUIDE_LIMIT_PERMISSIONS.flatMap((p) => [[`myDirect${guidePermVar(p)}`, 'not allowed'], [`myApprove${guidePermVar(p)}`, 'cannot approve']])),
    ...Object.fromEntries(GUIDE_ACT_PERMISSIONS.map((p) => [`myAct${guidePermVar(p)}`, 'not allowed'])),
    /**
     * From the instruments and categories (vars.ts): marketHoursRows, orderSizeRows and leverageRows (generated
     * table rows), and cryptoHours, fxHours, commodityHours, indexHours, stockHours (each class's commonest hours).
     */
    ...guideMarketVars(GUIDE_SAMPLE_INSTRUMENTS, GUIDE_SAMPLE_CATEGORIES),
    /** The all-clients hedge level (Admin → Hedging). */
    hedgeLevel: '0%',
    /** A price older than this is stale and refuses orders (market/quotes.ts QUOTE_MAX_AGE_MS). */
    staleSeconds: '30',
    /** The risk desk's long-held positions: open longer than this (desk/risk.ts). */
    longHeldDays: '90',
    /** Staff sign-in: the Telegram code's life, the Open Platform link's life and how many an hour. */
    staffCodeMinutes: '5',
    /** Whether a staff sign-in asks for a code after the password (STAFF_TG2FA). */
    staffSecondFactor: 'asked of everyone who has a linked Telegram or an authenticator app',
    staffLinkSeconds: '60',
    staffLinksPerHour: '10',
    /** Master followers: the smallest amount to assign, and the default stop loss (MASTER_LIMITS). */
    copyMinAmount: fmtUsd(COPY_LIMITS.minAmount, 0),
    masterStopLossDefault: pct(MASTER_LIMITS.defaultStopLossPct),
    // ---- Client lifecycle ----
    /** Trading credit on a retention agent's test account (TEST_ACCOUNT_CREDIT). */
    testAccountCredit: '$100,000',
    /** Hours after a declined demo request before the client may ask again (demo-requests.ts). */
    demoRetryHours: '24',
    /** Staff-set passwords and links (TEMP_PASSWORD_TTL_HOURS, WELCOME_LINK_TTL_HOURS, PASSWORD_RESET_TTL_MIN). */
    tempPasswordHours: '24',
    welcomeLinkHours: '72',
    resetLinkMinutes: '30',
    /** Shortest password staff may set for a client (staff-password.ts). */
    staffPasswordMin: '10',
    /** Log in as client: the link's life and the session's (CRM_LOGIN_LINK_TTL_SEC, CRM_LOGIN_SESSION_MINUTES). */
    clientLinkSeconds: '120',
    clientSessionMinutes: '60',
    /** Countries where clients can't sign in or deposit (RESTRICTED_COUNTRIES). */
    restrictedCountries: RESTRICTED_COUNTRY_NAMES,
    /** Compliance (COMPLIANCE_ENFORCE or Admin → SOF roll-out): whether the rules refuse, or only record. */
    complianceMode: 'recorded only: nothing is refused yet',
    eddThreshold: '$10,000',
    eddNotice: '$7,500',
    sofGraceDays: '14',
    sofBackfillDays: '30',
    sofValidMonths: '12',
    sofRecheck: '$50,000',
    sofStatementMonths: String(SOF_STATEMENT_MONTHS),
    poaMaxAgeDays: '90',
    expiryRemindDays: '30 and 7',
    /** Deposits. */
    wireDepositMin: `$${DEPOSIT_MIN_USD.bank_wire}`,
    cardDepositMin: `$${DEPOSIT_MIN_USD.card}`,
    cryptoDepositMin: '$250',
    cryptoPaymentMinutes: '45',
    /** How a client deposits crypto now: NOWPayments when it is set up, else a personal USDT address. */
    cryptoDepositHow: 'a personal USDT address per network, from the pool an admin imports',
    /** Crypto deposit networks: [network, confirmations, smallest deposit]. */
    depositNetworkRows: guideRows(guideNetworkRows()),
    /** Withdrawal limits and fees per method: [method, minimum, most at once, most in 24 hours, fee]. */
    wdLimitRows: guideRows(guideWithdrawRows(DEFAULT_WITHDRAW_LIMITS)),
    /** Wallet verification: hours the client has to send the transfer. */
    verifyHours: String(VERIFY_DEFAULT_HOURS),
    /** Trading fees per asset class: [asset class, commission per side, flat fee per trade, instruments]. */
    commissionRows: guideRows(guideStandardFeeRows()),
    // ---- Products ----
    copyMaxAmount: fmtUsd(COPY_LIMITS.maxAmount, 0),
    copyMaxCopies: String(COPY_LIMITS.maxRunningCopies),
    copyStopLossDefault: pct(COPY_LIMITS.defaultStopLossPct),
    /** Lead traders at this risk score or above take no new copiers. */
    copyRiskStop: String(COPY_LIMITS.noNewCopiersRisk),
    leadMinEquity: fmtUsd(LEAD_TRADER_REQUIREMENTS.minEquity, 0),
    leadMinDays: String(LEAD_TRADER_REQUIREMENTS.minTradingDays),
    smartMin: fmtUsd(SMART_LIMITS.minInvestment, 0),
    smartMax: fmtUsd(SMART_LIMITS.maxInvestment, 0),
    smartCap: fmtUsd(SMART_LIMITS.aumCap, 0),
    smartStopLossDefault: pct(SMART_LIMITS.defaultStopLossPct),
    smartLockTerms: guideLockTerms(LOCK_DEFAULTS.terms),
    smartFreeExitDays: String(LOCK_DEFAULTS.coolingOffDays),
    smartBonusCap: fmtUsd(LOCK_DEFAULTS.bonusCap, 0),
    /** Yield plans: [plan, yearly rate, smallest stake, most per client, offered]. */
    yieldPlanRows: guideRows(guideYieldRows([])),
    /** Earn tiers: [tier, volume, cashback]. */
    tierRows: guideRows(guideTierRows(REWARD_TIERS)),
    /** Crypto Pro (Products → Crypto Pro). */
    proOffered: 'hidden from clients: only staff and staff test accounts can open it',
    proSwapFee: `${DEFAULT_CRYPTO_PRO_SETTINGS.swapFeeBps / 100}%`,
    proTransferFee: fmtUsd(DEFAULT_CRYPTO_PRO_SETTINGS.transferFeeUsd, 0),
    proMinTransfer: fmtUsd(DEFAULT_CRYPTO_PRO_SETTINGS.minTransferUsd, 0),
    /** Meme coins (meme rules and the assessment). */
    memeQuestions: String(MEME_ASSESSMENT.length),
    memePassMark: String(MEME_PASS_MARK),
    memeRetryHours: String(MEME_RETRY_HOURS),
    memeHouseCap: `${fmtUsd(DEFAULT_MEME_RULES.risk.houseCapUsd, 0)} or ${DEFAULT_MEME_RULES.risk.houseCapLiquidityPct}% of the pool's liquidity, whichever is lower`,
    memeMaxListed: String(DEFAULT_MEME_RULES.listing.maxListed),
    memeAutoList: DEFAULT_MEME_RULES.listing.autoApprove ? 'on' : 'off',
    memeBreaker: `${DEFAULT_MEME_RULES.risk.breaker.move60sPct}% in 60 seconds or ${DEFAULT_MEME_RULES.risk.breaker.move15mPct}% in 15 minutes halts it for ${DEFAULT_MEME_RULES.risk.breaker.haltMinutes} minutes`,
    /** Push (Settings → Notifications). */
    pushOn: DEFAULT_PUSH_SETTINGS.enabled ? 'on' : 'off',
    pushQuietHours: `${DEFAULT_PUSH_SETTINGS.quietHours.start} to ${DEFAULT_PUSH_SETTINGS.quietHours.end} in the client's time zone`,
    pushDailyCap: String(DEFAULT_PUSH_SETTINGS.dailyCap),
    pushMarketingCap: String(DEFAULT_PUSH_SETTINGS.marketingDailyCap),
    agentPerClient: String(DEFAULT_AGENT_MESSAGE_SETTINGS.perClientPerDay),
    agentPerAgent: String(DEFAULT_AGENT_MESSAGE_SETTINGS.perAgentPerDay),
    agentFreeText: DEFAULT_AGENT_MESSAGE_SETTINGS.freeText ? 'allowed' : 'refused',
    /** Academy. */
    academyTracks: String(ACADEMY_TRACKS.length),
    academyLessons: String(ACADEMY_TRACKS.reduce((n, t) => n + t.lessons.length, 0)),
    // ---- Admin settings ----
    /** Product switches: [feature, for clients, blocked countries]. */
    featureRows: guideRows(guideFeatureRows(DEFAULT_FEATURE_SETTINGS)),
    /** Price routing: [asset class, providers first to last]. */
    routingRows: guideRows(guideRoutingRows(DEFAULT_ROUTING)),
    /** Deposit address pool: [network, free, assigned]. */
    poolRows: guideRows(guidePoolRows([])),
    /** CRM webhooks: tries before an event is given up (crm/webhooks.ts), and how many are given up now. */
    webhookMaxAttempts: '10',
    webhooksFailed: '0',
    /** Whether events go to the CRM (CRM_WEBHOOK_URL), and how old a signed CRM command may be (CRM_COMMAND_MAX_SKEW_SEC). */
    crmWebhooks: 'off: no CRM address is set, so nothing is sent',
    commandSkewSeconds: '300',
    /**
     * What each role may do (Staff & roles): [permission, support, dealer, senior dealer, finance,
     * compliance, retention dealer]. Read from staff_permissions; this default only says it couldn't be.
     */
    roleMatrixRows: guideRows([['The live matrix could not be read', '—', '—', '—', '—', '—', '—']]),
};
export const GUIDE_VAR_NAMES = new Set(Object.keys(GUIDE_VAR_DEFAULTS));
//# sourceMappingURL=types.js.map